import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Users, Plus, MagnifyingGlass, Trash, CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, Copy, ArrowsMerge, Envelope, ArrowsClockwise, X, DownloadSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ESignature from '../components/ESignature';
import MergeVolunteersDialog from '../components/MergeVolunteersDialog';
import EmailDuplicatesPanel from '../components/EmailDuplicatesPanel';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const statusColors = {
  active: 'bg-green-50 text-green-700 border-green-200',
  activo: 'bg-green-50 text-green-700 border-green-200',
  inactive: 'bg-stone-50 text-stone-600 border-stone-200',
  inactivo: 'bg-stone-50 text-stone-600 border-stone-200',
  in_study: 'bg-blue-50 text-blue-700 border-blue-200',
  excluded: 'bg-red-50 text-red-700 border-red-200',
  withdrawn: 'bg-amber-50 text-amber-700 border-amber-200',
};
const statusLabels = {
  es: { active: 'Activo', activo: 'Activo', inactive: 'Inactivo', inactivo: 'Inactivo', in_study: 'En estudio', excluded: 'Excluido', withdrawn: 'Retirado', pendiente: 'Pendiente' },
  en: { active: 'Active', activo: 'Active', inactive: 'Inactive', inactivo: 'Inactive', in_study: 'In study', excluded: 'Excluded', withdrawn: 'Withdrawn', pendiente: 'Pending' },
};

export default function Volunteers() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const canEdit = can('volunteers', 'edit');
  const canDelete = can('volunteers', 'delete');
  const [volunteers, setVolunteers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [filterDup, setFilterDup] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'email-duplicates'
  // URS-F398: ratios de asistencia por voluntario cargados en bulk para la
  // pagina visible. Formato: {volunteer_id: {ratio, asistidas, no_asiste,
  // anuladas, base}}. Voluntarios sin citas ejecutadas quedan fuera del dict.
  const [ratios, setRatios] = useState({});
  // Orden local por ratio dentro de la pagina visible: null | 'asc' | 'desc'
  const [ratioSort, setRatioSort] = useState(null);
  const searchTimer = useRef(null);

  const load = useCallback((p, q, dup, status) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
    if (q) params.set('search', q);
    if (dup) params.set('duplicates', 'true');
    if (status && status !== 'all') params.set('status', status);
    axios.get(`${API_URL}/api/volunteers?${params}`, { withCredentials: true })
      .then(r => {
        setVolunteers(r.data.data);
        setTotal(r.data.total);
        setTotalPages(r.data.pages);
        setPage(r.data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1, '', false, 'all'); }, [load]);

  // URS-F398: cargar ratios de asistencia en bulk para los voluntarios de la
  // pagina visible. Se dispara cada vez que cambia la lista.
  useEffect(() => {
    if (!volunteers.length) { setRatios({}); return; }
    const ids = volunteers.map(v => v.volunteer_id);
    axios.post(`${API_URL}/api/volunteers/attendance-ratios`, { volunteer_ids: ids }, { withCredentials: true })
      .then(r => setRatios(r.data || {}))
      .catch(() => setRatios({}));
  }, [volunteers]);

  // URS-F219: refresh the duplicates list when the tab regains focus so a DNI
  // correction made in another tab/window is reflected immediately.
  useEffect(() => {
    if (!filterDup) return;
    const onFocus = () => load(page, search, true, filterStatus);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [filterDup, page, search, filterStatus, load]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      load(1, val, filterDup, filterStatus);
    }, 350);
  };

  const toggleDup = () => {
    const next = !filterDup;
    setFilterDup(next);
    setPage(1);
    load(1, search, next, filterStatus);
  };

  const handleStatusFilter = (val) => {
    setFilterStatus(val);
    setPage(1);
    load(1, search, filterDup, val);
  };

  const goPage = (p) => {
    if (p < 1 || p > totalPages) return;
    load(p, search, filterDup, filterStatus);
  };

  const handleDismissPair = async () => {
    if (!dismissTarget) return;
    setDismissing(true);
    try {
      await axios.post(`${API_URL}/api/volunteers/duplicates/dismiss`, {
        id_a: dismissTarget.a.volunteer_id,
        id_b: dismissTarget.b.volunteer_id,
        reason: dismissReason.trim(),
      }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Par marcado como NO duplicado' : 'Pair marked as NOT a duplicate');
      setDismissTarget(null);
      setDismissReason('');
      load(page, search, filterDup, filterStatus);
    } catch (err) {
      toast.error(errText(err));
    }
    setDismissing(false);
  };

  const handleDelete = async (volId, signature) => {
    try {
      await axios.delete(`${API_URL}/api/volunteers/${volId}`, { withCredentials: true, data: { signature } });
      toast.success(lang === 'es' ? 'Voluntario eliminado' : 'Volunteer deleted');
      setDeleteTarget(null);
      load(page, search);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const startIdx = (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-6" data-testid="volunteers-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={28} weight="duotone" className="text-[#1f3e80]" />
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{t('volunteers')}</h1>
          <Badge variant="outline" className="text-xs font-mono">{total}</Badge>
        </div>
        <Button data-testid="create-volunteer-btn"
          onClick={() => navigate('/volunteers/new')}
          disabled={!canEdit}
          title={!canEdit ? (lang === 'es' ? 'Sin permiso de edicion' : 'No edit permission') : ''}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 disabled:bg-stone-300 disabled:hover:bg-stone-300">
          <Plus size={16} />
          {t('create_volunteer')}
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="volunteer-search" placeholder={lang === 'es' ? 'Buscar por nombre, ID, DNI, email o teléfono...' : 'Search by name, ID, DNI, email or phone...'} value={search} onChange={e => handleSearch(e.target.value)} className="pl-9 bg-white border-stone-200" />
          </div>
          <Select value={filterStatus} onValueChange={handleStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-36 bg-white border-stone-200 h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'es' ? 'Todos' : 'All'}</SelectItem>
              <SelectItem value="activo">{lang === 'es' ? 'Activos' : 'Active'}</SelectItem>
              <SelectItem value="inactivo">{lang === 'es' ? 'Inactivos' : 'Inactive'}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant={filterDup ? 'default' : 'outline'} data-testid="filter-duplicates-btn"
            onClick={toggleDup}
            className={`rounded-sm gap-2 text-sm ${filterDup ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-stone-600 border-stone-200'}`}>
            <Copy size={14} />
            {lang === 'es' ? 'Duplicados' : 'Duplicates'}
            {filterDup && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-white/20 text-white border-white/30">{total}</Badge>}
          </Button>
          {filterDup && (
            <Button variant="outline" data-testid="refresh-duplicates-btn"
              onClick={() => load(page, search, true, filterStatus)}
              title={lang === 'es' ? 'Recalcular la lista de duplicados' : 'Refresh duplicates list'}
              className="rounded-sm gap-2 text-sm text-stone-600 border-stone-200">
              <ArrowsClockwise size={14} />
              {lang === 'es' ? 'Refrescar' : 'Refresh'}
            </Button>
          )}
          <Button variant="outline" data-testid="filter-email-duplicates-btn"
            onClick={() => setViewMode('email-duplicates')}
            className="rounded-sm gap-2 text-sm text-stone-600 border-stone-200">
            <Envelope size={14} />
            {lang === 'es' ? 'Mismo email' : 'Same email'}
          </Button>
          <Button variant="outline" data-testid="export-volunteers-xlsx-btn"
            onClick={() => {
              // URS-F441: exportar el listado actual (respeta filtros) a Excel.
              const params = new URLSearchParams({ lang });
              if (search) params.set('search', search);
              if (filterDup) params.set('duplicates', 'true');
              if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
              const url = `${API_URL}/api/volunteers/export.xlsx?${params.toString()}`;
              window.open(url, '_blank');
            }}
            title={lang === 'es' ? 'Exportar el listado actual a Excel (respeta filtros)' : 'Export current list to Excel (respects filters)'}
            className="rounded-sm gap-2 text-sm text-stone-600 border-stone-200">
            <DownloadSimple size={14} />
            {lang === 'es' ? 'Exportar' : 'Export'}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {viewMode === 'email-duplicates' ? (
          <EmailDuplicatesPanel lang={lang} onClose={() => setViewMode('list')} />
        ) : loading ? (
          <div className="p-12 text-center"><p className="text-sm text-stone-500">{t('loading')}</p></div>
        ) : volunteers.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={40} weight="duotone" className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">{search ? (lang === 'es' ? 'Sin resultados para esta búsqueda' : 'No results for this search') : t('no_records')}</p>
          </div>
        ) : (
          <Table className="dense-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">ID</TableHead>
                <TableHead>{t('name')}</TableHead>
                <TableHead className="w-32">DNI</TableHead>
                <TableHead>{t('status')}</TableHead>
                <TableHead className="w-32 text-center">{t('documentation')}</TableHead>
                <TableHead className="w-28 text-center">
                  <button
                    type="button"
                    onClick={() => setRatioSort(p => p === 'asc' ? 'desc' : p === 'desc' ? null : 'asc')}
                    data-testid="ratio-sort-toggle"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:text-[#1f3e80] transition-colors"
                    title={lang === 'es' ? 'Ordenar por ratio dentro de la pagina' : 'Sort by ratio within page'}
                  >
                    {lang === 'es' ? 'Ratio' : 'Ratio'}
                    <span className="text-stone-400 text-[10px]">
                      {ratioSort === 'asc' ? '\u2191' : ratioSort === 'desc' ? '\u2193' : '\u2195'}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                let rows = volunteers;
                if (ratioSort) {
                  // Sort local dentro de la pagina. Voluntarios sin datos
                  // suficientes (base=0) van SIEMPRE al final para no
                  // ensuciar el podio de los peores/mejores.
                  const withRatio = [];
                  const withoutData = [];
                  for (const v of volunteers) {
                    const r = ratios[v.volunteer_id];
                    if (r && r.ratio !== null && r.ratio !== undefined) withRatio.push(v);
                    else withoutData.push(v);
                  }
                  withRatio.sort((a, b) => {
                    const ra = ratios[a.volunteer_id].ratio;
                    const rb = ratios[b.volunteer_id].ratio;
                    return ratioSort === 'asc' ? ra - rb : rb - ra;
                  });
                  rows = [...withRatio, ...withoutData];
                }
                return rows.map((v, idx) => {
                const prevVol = idx > 0 ? rows[idx - 1] : null;
                const nextVol = idx < rows.length - 1 ? rows[idx + 1] : null;
                const getGroup = (vol) => vol?.duplicate_dni_group || vol?.duplicate_name_group || null;
                const vGroup = getGroup(v);
                const sameGroupPrev = prevVol && vGroup && getGroup(prevVol) === vGroup;
                const sameGroupNext = nextVol && vGroup && getGroup(nextVol) === vGroup;
                const isFirstOfPair = filterDup && !sameGroupPrev && sameGroupNext;
                // URS-F223: compare birth dates within a pair so the operator
                // can spot at a glance whether the duplicate is plausible
                // (same DOB) or a probable false-positive that survived the
                // stricter filter (e.g. siblings with very close names).
                const fnac = v.personal_data?.fecha_nacimiento || '';
                let dobMismatch = false;
                if (filterDup && vGroup) {
                  const peer = sameGroupPrev ? prevVol : (sameGroupNext ? nextVol : null);
                  const peerFnac = peer?.personal_data?.fecha_nacimiento || '';
                  if (fnac && peerFnac && fnac.slice(0,10) !== peerFnac.slice(0,10)) dobMismatch = true;
                }
                return (
                <TableRow key={v.volunteer_id} data-testid={`volunteer-row-${v.volunteer_id}`} className={`hover:bg-stone-50 cursor-pointer ${sameGroupPrev && filterDup ? 'border-t-0 bg-stone-50/50' : ''}`} onClick={() => navigate(`/volunteers/${v.volunteer_id}`)}>
                  <TableCell className="font-mono text-xs text-stone-600">{v.volunteer_id}</TableCell>
                  <TableCell className="text-sm font-medium text-stone-900">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{v.display_name}</span>
                      {filterDup && fnac && (
                        <Badge variant="outline" data-testid={`vol-dob-${v.volunteer_id}`}
                          className={`text-[10px] font-mono ${dobMismatch ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-stone-50 text-stone-600 border-stone-200'}`}
                          title={dobMismatch ? (lang === 'es' ? 'Fechas de nacimiento distintas en el par - probable falso positivo' : 'Different birth dates in the pair - likely false positive') : ''}>
                          {dobMismatch && <span className="mr-1">⚠</span>}
                          {fnac.slice(0,10)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-stone-500">{v.personal_data?.dni || ''}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-xs ${statusColors[v.status] || ''}`}>{statusLabels[lang]?.[v.status] || v.status}</Badge></TableCell>
                  <TableCell className="text-center">
                    <span className={`inline-block w-3 h-3 rounded-full ${v.doc_status === 'green' ? 'bg-green-500' : v.doc_status === 'yellow' ? 'bg-amber-400' : 'bg-red-500'}`} />
                  </TableCell>
                  <TableCell className="text-center" data-testid={`vol-ratio-cell-${v.volunteer_id}`}>
                    {(() => {
                      const r = ratios[v.volunteer_id];
                      if (!r || r.ratio === null || r.ratio === undefined) {
                        return <span className="text-[11px] text-stone-400">—</span>;
                      }
                      const cls = r.ratio >= 80
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : r.ratio >= 50
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-red-50 text-red-700 border-red-200';
                      const tooltip = lang === 'es'
                        ? `${r.asistidas} asistidas / ${r.base} citas ejecutadas (${r.no_asiste} no asisti\u00f3, ${r.anuladas} anuladas)`
                        : `${r.asistidas} attended / ${r.base} executed appts (${r.no_asiste} no-show, ${r.anuladas} cancelled)`;
                      return (
                        <Badge variant="outline" className={`text-xs font-mono ${cls}`} title={tooltip}>
                          {r.ratio}%
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {isFirstOfPair && isAdmin && (
                        <Button variant="outline" size="sm" data-testid={`merge-volunteer-${v.volunteer_id}`}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMergeTarget({ a: v, b: nextVol }); }}
                          className="h-8 px-3 text-[11px] text-emerald-600 hover:text-emerald-700 border-emerald-300 hover:bg-emerald-50 gap-1.5">
                          <ArrowsMerge size={14} /> {lang === 'es' ? 'Fusionar' : 'Merge'}
                        </Button>
                      )}
                      {isFirstOfPair && isAdmin && (
                        <Button variant="outline" size="sm" data-testid={`dismiss-duplicate-${v.volunteer_id}`}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDismissTarget({ a: v, b: nextVol }); }}
                          title={lang === 'es' ? 'Confirmar que NO son duplicados y quitar el par de la lista' : 'Confirm they are NOT duplicates and remove the pair from the list'}
                          className="h-8 px-3 text-[11px] text-stone-600 hover:text-stone-700 border-stone-300 hover:bg-stone-50 gap-1.5">
                          <X size={14} /> {lang === 'es' ? 'No es duplicado' : 'Not a duplicate'}
                        </Button>
                      )}
                      {(isAdmin || canDelete) && (
                        <Button variant="ghost" size="icon" data-testid={`delete-volunteer-${v.volunteer_id}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(v); }} className="h-7 w-7 text-stone-400 hover:text-red-600">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );});
              })()}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2" data-testid="pagination-controls">
          <p className="text-sm text-stone-500">
            {lang === 'es' ? `${startIdx}-${endIdx} de ${total}` : `${startIdx}-${endIdx} of ${total}`}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => goPage(1)} data-testid="page-first" title={lang === 'es' ? 'Primera página' : 'First page'}>
              <CaretDoubleLeft size={14} />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="page-prev">
              <CaretLeft size={14} />
            </Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let p;
              if (totalPages <= 7) {
                p = i + 1;
              } else if (page <= 4) {
                p = i + 1;
              } else if (page >= totalPages - 3) {
                p = totalPages - 6 + i;
              } else {
                p = page - 3 + i;
              }
              return (
                <Button key={p} variant={p === page ? 'default' : 'outline'} size="icon"
                  className={`h-8 w-8 text-xs ${p === page ? 'bg-[#1f3e80] text-white' : ''}`}
                  onClick={() => goPage(p)} data-testid={`page-${p}`}>
                  {p}
                </Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => goPage(page + 1)} data-testid="page-next">
              <CaretRight size={14} />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => goPage(totalPages)} data-testid="page-last" title={lang === 'es' ? 'Última página' : 'Last page'}>
              <CaretDoubleRight size={14} />
            </Button>
          </div>
        </div>
      )}

      <ESignature
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSigned={(signature) => handleDelete(deleteTarget.volunteer_id, signature)}
        action={lang === 'es' ? `Eliminar voluntario ${deleteTarget?.volunteer_id || ''}` : `Delete volunteer ${deleteTarget?.volunteer_id || ''}`}
        resource={`volunteer/${deleteTarget?.volunteer_id || ''}`}
        lang={lang}
      />

      <MergeVolunteersDialog
        open={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        volunteerA={mergeTarget?.a}
        volunteerB={mergeTarget?.b}
        lang={lang}
        onMerged={() => { setMergeTarget(null); load(page, search, filterDup, filterStatus); }}
      />

      <Dialog open={!!dismissTarget} onOpenChange={(o) => { if (!o) { setDismissTarget(null); setDismissReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="dismiss-duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {lang === 'es' ? 'Marcar como NO duplicados' : 'Mark as NOT duplicates'}
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Confirmas que estas dos personas son distintas a pesar de compartir DNI. El par desaparecera de la lista de Duplicados de forma permanente. Los voluntarios NO se eliminan.'
                : 'You confirm these two people are different despite sharing the DNI. The pair will be permanently removed from the Duplicates list. Volunteers are NOT deleted.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-[#1f3e80]">{dismissTarget?.a?.volunteer_id}</span>
                <span className="text-stone-700">{dismissTarget?.a?.display_name}</span>
                <span className="font-mono text-stone-500 ml-auto">{dismissTarget?.a?.personal_data?.dni}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-[#1f3e80]">{dismissTarget?.b?.volunteer_id}</span>
                <span className="text-stone-700">{dismissTarget?.b?.display_name}</span>
                <span className="font-mono text-stone-500 ml-auto">{dismissTarget?.b?.personal_data?.dni}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">
                {lang === 'es' ? 'Motivo (opcional pero recomendado)' : 'Reason (optional but recommended)'}
              </label>
              <Input
                data-testid="dismiss-reason-input"
                value={dismissReason}
                onChange={e => setDismissReason(e.target.value)}
                placeholder={lang === 'es' ? 'Ej: DNI introducido por error, personas distintas' : 'E.g.: DNI typed by mistake, different people'}
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => { setDismissTarget(null); setDismissReason(''); }} disabled={dismissing}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button variant="default" size="sm" data-testid="confirm-dismiss-duplicate"
              onClick={handleDismissPair} disabled={dismissing}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a]">
              {dismissing
                ? (lang === 'es' ? 'Guardando...' : 'Saving...')
                : (lang === 'es' ? 'Confirmar - No son duplicados' : 'Confirm - Not duplicates')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
